import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Users, TrendingUp, Calendar, Briefcase, DollarSign,
  ArrowUpRight, ArrowDownRight, Clock, Activity, Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMDashboardPage = () => {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  const stats = [
    {
      title: 'Total Customers',
      value: dashboard?.customers?.total || 0,
      change: dashboard?.customers?.new_this_month || 0,
      changeLabel: 'new this month',
      icon: Users,
      color: 'bg-blue-500',
      link: '/crm/customers'
    },
    {
      title: 'Pipeline Value',
      value: `$${(dashboard?.opportunities?.pipeline_value || 0).toLocaleString()}`,
      change: dashboard?.opportunities?.open || 0,
      changeLabel: 'open deals',
      icon: DollarSign,
      color: 'bg-green-500',
      link: '/crm/opportunities'
    },
    {
      title: 'Won This Month',
      value: `$${(dashboard?.opportunities?.won_revenue_month || 0).toLocaleString()}`,
      change: dashboard?.opportunities?.won_deals_month || 0,
      changeLabel: 'deals closed',
      icon: TrendingUp,
      color: 'bg-emerald-500',
      link: '/crm/opportunities'
    },
    {
      title: 'Active Projects',
      value: dashboard?.projects?.active || 0,
      icon: Briefcase,
      color: 'bg-purple-500',
      link: '/crm/projects'
    },
    {
      title: 'Upcoming Bookings',
      value: dashboard?.bookings?.upcoming || 0,
      change: dashboard?.bookings?.today || 0,
      changeLabel: 'today',
      icon: Calendar,
      color: 'bg-orange-500',
      link: '/crm/bookings'
    },
    {
      title: 'New Leads',
      value: dashboard?.leads?.new || 0,
      change: dashboard?.leads?.total || 0,
      changeLabel: 'total',
      icon: Target,
      color: 'bg-red-500',
      link: '/leads'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">CRM Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">Overview of your sales and customer management</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-lg dark:hover:shadow-gray-800/50 transition-shadow cursor-pointer" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                      <p className="text-2xl font-bold mt-1 dark:text-white">{stat.value}</p>
                      {stat.change !== undefined && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <span className="text-green-600 dark:text-green-400 font-medium">+{stat.change}</span> {stat.changeLabel}
                        </p>
                      )}
                    </div>
                    <div className={`${stat.color} p-3 rounded-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold dark:text-white">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link 
                to="/crm/customers" 
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                data-testid="quick-action-customers"
              >
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="dark:text-gray-200">Manage Customers</span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-gray-400" />
              </Link>
              <Link 
                to="/crm/opportunities" 
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                data-testid="quick-action-opportunities"
              >
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="dark:text-gray-200">View Sales Pipeline</span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-gray-400" />
              </Link>
              <Link 
                to="/crm/bookings" 
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                data-testid="quick-action-bookings"
              >
                <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <span className="dark:text-gray-200">Schedule Meeting</span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-gray-400" />
              </Link>
              <Link 
                to="/crm/reports" 
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                data-testid="quick-action-reports"
              >
                <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="dark:text-gray-200">View Reports</span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-gray-400" />
              </Link>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold dark:text-white">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.recent_activities?.length > 0 ? (
                <div className="space-y-4">
                  {dashboard.recent_activities.map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Activity className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm dark:text-gray-200">{activity.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{activity.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600 dark:text-gray-300" />
                  <p>No recent activity</p>
                  <p className="text-sm">Activities will appear here as you use the CRM</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CRMDashboardPage;
